import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RefreshCw, AlertCircle, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDashboardFiltersStore } from '@/store/dashboardFiltersStore';
import type { DeviceData } from '../api/dashboard.schemas';

interface DeviceChartProps {
  data?: DeviceData;
  loading?: boolean;
  error?: Error;
  height?: number;
}

/**
 * DeviceChart Component
 * 
 * Displays a pie chart showing the most used devices for verification.
 * Helps understand user device preferences and optimize accordingly.
 */
export function DeviceChart({
  data,
  loading = false,
  error,
  height = 300
}: DeviceChartProps) {
  const { t } = useTranslation();
  const activeDevice = useDashboardFiltersStore((s) => s.filters.device);
  const toggleFilter = useDashboardFiltersStore((s) => s.toggleFilter);
  
  // Loading state
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">{t('dashboard.topDevicesUsed')}</CardTitle>
          </div>
          <CardDescription>{t('dashboard.mostPopularDevices')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center" style={{ height }}>
            <div className="text-center text-muted-foreground">
              <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
              {t('dashboard.loadingChart')}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">{t('dashboard.chartError')}</CardTitle>
          </div>
          <CardDescription>{t('dashboard.failedToLoadDeviceData')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center" style={{ height }}>
            <div className="text-center space-y-4">
              <div className="mx-auto w-12 h-12 bg-gradient-to-br from-destructive/10 to-destructive/5 rounded-xl flex items-center justify-center">
                <AlertCircle className="h-6 w-6 text-destructive/60" />
              </div>
              <div className="space-y-2">
                <h3 className="font-medium">{t('dashboard.unableToDisplayChart')}</h3>
                <p className="text-sm text-muted-foreground">{error.message}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!data || data.data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">{t('dashboard.topDevicesUsed')}</CardTitle>
          </div>
          <CardDescription>{t('dashboard.mostPopularDevices')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center" style={{ height }}>
            <div className="text-center text-muted-foreground">
              <Smartphone className="h-6 w-6 mx-auto mb-2" />
              <p>{t('dashboard.noDeviceData')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Sort data by value (highest to lowest) and calculate total
  const sortedData = [...data.data].sort((a, b) => b.value - a.value);
  const total = data.data.reduce((sum, item) => sum + item.value, 0);
  const colors = [
    'bg-blue-500',
    'bg-green-500', 
    'bg-yellow-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500'
  ];

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center gap-2">
          <Smartphone className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">{t('dashboard.topDevicesUsed')}</CardTitle>
        </div>
        <CardDescription>
          {t('dashboard.mostPopularDevicesWithTotal', { total })}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 space-y-3 overflow-y-auto">
          {sortedData.map((item, index) => {
            const percentage = total > 0 ? (item.value / total) * 100 : 0;
            const colorClass = colors[index % colors.length];
            // Use the full (untruncated) name as the cross-filter value
            // so the server can exact-match it against the rollup keys.
            // `rawName` is populated by `useDashboardData` from the raw
            // API response; falls back to `name` for older shapes.
            const filterValue = (item as { rawName?: string }).rawName ?? item.name;
            const isActive = activeDevice === filterValue;
            return (
              <button
                type="button"
                key={index}
                onClick={() => toggleFilter('device', filterValue)}
                className={`w-full text-left flex items-center justify-between p-3 rounded-lg border transition-colors hover:bg-muted/40 ${
                  isActive ? 'border-primary ring-1 ring-primary/40 bg-primary/5' : ''
                }`}
                aria-pressed={isActive}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${colorClass}`} />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{item.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {percentage.toFixed(1)}% {t('dashboard.ofTotal')}
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <p className="font-semibold">{item.value}</p>
                  <p className="text-xs text-muted-foreground">{t('dashboard.uses')}</p>
                </div>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}